import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { needsRetrigger, isStageApplicable, stageCompletion, PIPELINE_STAGES } from "@/lib/pipelineStages";

const SB_URL    = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY  = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
const R2_BASE   = ((import.meta.env.VITE_R2_PUBLIC_URL as string) ?? "").replace(/\/$/, "");

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

type PipelineStatus = "pending" | "processing" | "complete" | "failed" | string | null;

interface SourcePhoto {
  angle: string;
  url:   string;
}

interface PipelineProduct {
  id: string;
  name: string;
  asset_status:         PipelineStatus;
  pbr_status:           PipelineStatus;
  geometry_status:      PipelineStatus;
  glb_assembly_status:  PipelineStatus;
  qa_status:            PipelineStatus;
  qa_score:             number | null;
  qa_ssim_score:        number | null;
  qa_ai_verdict:        string | null;
  qa_ai_notes:          string | null;
  qa_render_url:        string | null;
  qa_flags:             string[] | null;
  source_photos:        SourcePhoto[] | null;
  pbr_confidence:       number | null;
  pipeline_cost_cents:  Record<string, number> | null;
  cad_dimensions_json:  Record<string, unknown> | null;
  consumer_specs_json:  Record<string, unknown> | null;
  // Decide which stages apply to this product — a parametric pergola never
  // runs PBR, so scoring it out of a global 4 reports it as permanently
  // stalled. See src/lib/pipelineStages.ts.
  geometry_source:      string | null;
  material_source:      string | null;
  created_at: string;
  manufacturer_name?: string;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                              */
/* ------------------------------------------------------------------ */

const STATUS_META: Record<string, { label: string; classes: string }> = {
  pending:             { label: "Pending",             classes: "bg-yellow-900/40 text-yellow-400 border-yellow-800" },
  processing:          { label: "Processing",          classes: "bg-blue-900/40 text-blue-400 border-blue-800" },
  complete:            { label: "Complete",             classes: "bg-green-900/40 text-green-400 border-green-800" },
  // asset_status terminal states. `ready` is the APPROVED path
  // (process-qa-validation:755, written with active=true); `complete` above is
  // the review path (:776). Without a `ready` entry every published product
  // fell through to the untyped grey fallback and, worse, matched no filter
  // chip at all, so the one status an operator most wants to see was the one
  // the page could not select for.
  ready:               { label: "AR Ready",             classes: "bg-green-900/40 text-green-400 border-green-800" },
  // Stage terminal states that are successes, not omissions — see
  // src/lib/pipelineStages.ts for where they come from.
  skipped:             { label: "Skipped",              classes: "bg-gray-800 text-gray-400 border-gray-700" },
  complete_fallback:   { label: "Complete (fallback)",  classes: "bg-amber-900/40 text-amber-400 border-amber-800" },
  failed:              { label: "Failed",               classes: "bg-red-900/40 text-red-400 border-red-800" },
  approved:            { label: "Approved",             classes: "bg-green-900/40 text-green-400 border-green-800" },
  review:              { label: "Review",               classes: "bg-amber-900/40 text-amber-400 border-amber-800" },
  rejected:            { label: "Rejected",             classes: "bg-red-900/40 text-red-400 border-red-800" },
  // Pre-ingestion quality gate (trigger-geometry-pipeline / orchestrate-pipeline):
  // rejected before InstantMesh/Zero123 reconstruction ever ran, distinct from a
  // pipeline failure — the fix is "upload more photos", not "retry".
  insufficient_photos: { label: "Insufficient Photos",  classes: "bg-orange-900/40 text-orange-400 border-orange-800" },
};

function QaBadge({ status, score }: { status: PipelineStatus; score: number | null }) {
  if (!status) {
    return <span className="inline-flex items-center rounded-full border border-gray-800 bg-gray-900/40 px-2 py-0.5 text-xs text-gray-600">—</span>;
  }
  const isActive = status === "pending" || status === "processing";
  const meta = STATUS_META[status] ?? { label: status, classes: "bg-gray-800 text-gray-400 border-gray-700" };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${meta.classes}`}>
      {isActive && <span className="inline-block h-1.5 w-1.5 rounded-full bg-current animate-pulse" />}
      {meta.label}
      {score != null && !isActive && (
        <span className="ml-0.5 opacity-70">{Math.round(score * 100)}%</span>
      )}
    </span>
  );
}

/** A stage this product's pipeline does not include. Visually distinct from
 *  StatusBadge's "—" (which means *not started yet*): "n/a" means nothing is
 *  coming, so an operator does not sit waiting for a stage that will never run. */
function NotApplicable({ reason }: { reason: string }) {
  return (
    <span
      title={reason}
      className="inline-flex items-center rounded-full border border-gray-800 bg-gray-900/20 px-2 py-0.5 text-xs text-gray-600 italic"
    >
      n/a
    </span>
  );
}

/** Completion against the stages that apply to THIS product. A pergola reads
 *  3/3 rather than 3/4 — the whole point of the M5b analytics fix, and the
 *  cheapest way to make it visible instead of buried in a predicate. */
function StageProgress({ product }: { product: PipelineProduct }) {
  const { complete, total, done } = stageCompletion(product);
  const skipped = PIPELINE_STAGES.length - total;
  return (
    <span
      title={
        skipped > 0
          ? `${complete} of ${total} applicable stages complete. ${skipped} stage${skipped === 1 ? "" : "s"} not part of this product's pipeline.`
          : `${complete} of ${total} stages complete.`
      }
      className={
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs tabular-nums " +
        (done
          ? "border-emerald-800 bg-emerald-900/30 text-emerald-400"
          : "border-gray-700 bg-gray-900/60 text-gray-400")
      }
    >
      {complete}/{total}
    </span>
  );
}

function StatusBadge({ status, title }: { status: PipelineStatus; title?: string }) {
  if (!status) {
    return (
      <span className="inline-flex items-center rounded-full border border-gray-800 bg-gray-900/40 px-2 py-0.5 text-xs text-gray-600">
        —
      </span>
    );
  }
  const meta = STATUS_META[status] ?? {
    label: status,
    classes: "bg-gray-800 text-gray-400 border-gray-700",
  };
  return (
    <span
      title={title}
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${meta.classes} ${title ? "cursor-help" : ""}`}
    >
      {meta.label}
    </span>
  );
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatCost(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/* ------------------------------------------------------------------ */
/* Main Page                                                            */
/* ------------------------------------------------------------------ */

// Stage applicability lives in src/lib/pipelineStages.ts — see the note there
// on why PBR keys on material_source rather than geometry_source.

/* ------------------------------------------------------------------ */
/* QA helpers                                                           */
/* ------------------------------------------------------------------ */

const FLAG_LABELS: Record<string, string> = {
  "render_mode:software_v1": "Scored against real 3D render",
  "ssim_self_reference":     "SSIM self-reference",
  "ai_error":                "AI check failed",
  "no_source_photos":        "No source photos",
};

function humanizeFlag(f: string): string {
  if (FLAG_LABELS[f]) return FLAG_LABELS[f];
  if (f.startsWith("render_unavailable:")) return "Render unavailable (no service)";
  return f;
}

/* ------------------------------------------------------------------ */
/* QA Review Modal                                                      */
/* ------------------------------------------------------------------ */

function QaReviewModal({
  product,
  jwt,
  onClose,
  onAction,
}: {
  product:   PipelineProduct;
  jwt:       string;
  onClose:   () => void;
  onAction:  (id: string, action: "approved" | "rejected") => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [zoomUrl, setZoomUrl] = useState<string | null>(null);
  const [threeQtrError, setThreeQtrError] = useState(false);
  const [lowResHint, setLowResHint] = useState<{ w: number; h: number } | null>(null);

  const isRenderMode = product.qa_flags?.includes("render_mode:software_v1") ?? false;

  const PREFERRED = ["Front", "3/4 Left", "3/4 Right", "Back", "Left", "Right", "Top", "Detail"];
  const refPhoto = product.source_photos
    ? (PREFERRED.map((a) => product.source_photos!.find((p) => p.angle === a)).find(Boolean) ?? product.source_photos[0])
    : null;

  const cleanedUrl   = R2_BASE ? `${R2_BASE}/processed-assets/${product.id}/pbr/front_cleaned.png` : null;
  const threeQtrUrl  = R2_BASE ? `${R2_BASE}/processed-assets/${product.id}/qa/render_three_quarter.png` : null;

  // Low-res content-bbox hint
  useEffect(() => {
    if (!cleanedUrl) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width  = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(img, 0, 0);
        const { data } = ctx.getImageData(0, 0, img.naturalWidth, img.naturalHeight);
        let minX = img.naturalWidth, maxX = 0, minY = img.naturalHeight, maxY = 0;
        for (let y = 0; y < img.naturalHeight; y++) {
          for (let x = 0; x < img.naturalWidth; x++) {
            if (data[(y * img.naturalWidth + x) * 4 + 3] > 16) {
              if (x < minX) minX = x; if (x > maxX) maxX = x;
              if (y < minY) minY = y; if (y > maxY) maxY = y;
            }
          }
        }
        if (maxX > minX && maxY > minY) {
          const w = maxX - minX + 1;
          const h = maxY - minY + 1;
          if (Math.min(w, h) < 400) setLowResHint({ w, h });
        }
      } catch { /* CORS/canvas — silent */ }
    };
    img.onerror = () => { /* silent */ };
    img.src = cleanedUrl;
  }, [cleanedUrl]);

  async function handleApprove() {
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`${SB_URL}/rest/v1/products?id=eq.${product.id}`, {
        method:  "PATCH",
        headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}`, "Content-Type": "application/json", Prefer: "return=minimal" },
        body:    JSON.stringify({
          qa_status:      "approved",
          qa_approved_by: "admin",
          qa_approved_at: new Date().toISOString(),
          asset_status:   "ready",
          active:         true,
        }),
      });
      if (!res.ok) throw new Error(`PATCH failed (${res.status})`);
      onAction(product.id, "approved");
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to approve");
    } finally {
      setBusy(false);
    }
  }

  async function handleReject() {
    setBusy(true); setErr(null);
    try {
      const patchRes = await fetch(`${SB_URL}/rest/v1/products?id=eq.${product.id}`, {
        method:  "PATCH",
        headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}`, "Content-Type": "application/json", Prefer: "return=minimal" },
        body:    JSON.stringify({ qa_status: "rejected" }),
      });
      if (!patchRes.ok) throw new Error(`PATCH failed (${patchRes.status})`);
      await fetch(`${SB_URL}/functions/v1/trigger-qa-validation`, {
        method:  "POST",
        headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}`, "Content-Type": "application/json" },
        body:    JSON.stringify({ product_id: product.id }),
      });
      onAction(product.id, "rejected");
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to reject");
    } finally {
      setBusy(false);
    }
  }

  // Score values
  const score       = product.qa_score        != null ? `${Math.round(product.qa_score * 100)}%`        : "—";
  const ssimRaw     = product.qa_ssim_score   ?? null;
  const ssimDisplay = ssimRaw                  != null ? `${Math.round(ssimRaw * 100)}%`                : "—";
  const ssimNorm    = ssimRaw                  != null ? Math.max(0, Math.min(1, (ssimRaw - 0.2) / 0.6)) : null;
  const pbrConf     = product.pbr_confidence  ?? null;
  const aiVerdict   = product.qa_ai_verdict   ?? null;
  const aiVal       = aiVerdict === "pass" ? 1 : aiVerdict === "review" ? 0.5 : aiVerdict != null ? 0 : null;
  const verdictColor = aiVerdict === "pass" ? "text-green-400" : aiVerdict === "fail" ? "text-red-400" : "text-amber-400";

  const scoreBarItems = isRenderMode
    ? [
        { label: "PBR confidence", weight: "30%", value: pbrConf },
        { label: "AI verdict",     weight: "50%", value: aiVal },
        { label: "SSIM",           weight: "20%", value: ssimNorm },
      ]
    : [
        { label: "PBR confidence", weight: "50%", value: pbrConf },
        { label: "AI verdict",     weight: "50%", value: aiVal },
      ];

  // Dimensions
  type CadDims = { width_m?: number; height_m?: number; depth_m?: number };
  const dims = product.cad_dimensions_json as CadDims | null;
  function fmtM(v?: number) { return v != null ? `${v.toFixed(3)} m` : "—"; }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Click-to-zoom lightbox */}
      {zoomUrl && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90"
          onClick={() => setZoomUrl(null)}
        >
          <img src={zoomUrl} alt="Zoom" className="max-w-[90vw] max-h-[90vh] rounded-xl object-contain" />
        </div>
      )}

      <div className="relative w-full max-w-3xl rounded-2xl border border-gray-700 bg-gray-950 p-6 space-y-5 overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-white">QA Review — {product.name}</h2>
            <p className="text-sm text-gray-400 mt-0.5">Review the render comparison and approve or reject.</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 text-xl leading-none">✕</button>
        </div>

        {/* Render-mode indicator chip */}
        <div>
          {isRenderMode ? (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-green-700 bg-green-900/30 px-3 py-1 text-xs font-medium text-green-400">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-green-400" />
              Scored against real 3D render
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full border border-gray-700 bg-gray-800/60 px-3 py-1 text-xs font-medium text-gray-500">
              Legacy: photo self-check (SSIM not meaningful)
            </span>
          )}
        </div>

        {/* Side-by-side images */}
        <div className="grid grid-cols-2 gap-3">
          {/* Source / cleaned reference */}
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Source photo</p>
            {cleanedUrl ? (
              <img
                src={cleanedUrl}
                alt="Product (cleaned)"
                className="w-full rounded-lg border border-gray-800 object-contain bg-gray-900 cursor-zoom-in"
                style={{ maxHeight: 240 }}
                onClick={() => setZoomUrl(cleanedUrl)}
                onError={(e) => {
                  const fb = refPhoto?.url;
                  const el = e.currentTarget as HTMLImageElement;
                  if (fb && el.src !== fb) el.src = fb;
                }}
              />
            ) : refPhoto ? (
              <img
                src={refPhoto.url}
                alt="Reference"
                className="w-full rounded-lg border border-gray-800 object-contain bg-gray-900 cursor-zoom-in"
                style={{ maxHeight: 240 }}
                onClick={() => setZoomUrl(refPhoto.url)}
              />
            ) : (
              <div className="rounded-lg border border-gray-800 bg-gray-900 h-40 flex items-center justify-center text-xs text-gray-600">No photo</div>
            )}
            {lowResHint && (
              <div className="rounded-lg border border-amber-700/40 bg-amber-900/20 px-3 py-2">
                <p className="text-xs text-amber-400">
                  Product occupies only {lowResHint.w}×{lowResHint.h}px of the source photo — textures will look sharper with a product image ≥800px (manufacturer press photos work best)
                </p>
              </div>
            )}
          </div>

          {/* 3D renders */}
          <div className="space-y-2">
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">3D render (front)</p>
              {product.qa_render_url ? (
                <img
                  src={product.qa_render_url}
                  alt="3D Render (front)"
                  className="w-full rounded-lg border border-gray-800 object-contain bg-gray-900 cursor-zoom-in"
                  style={{ maxHeight: 240 }}
                  onClick={() => setZoomUrl(product.qa_render_url!)}
                />
              ) : (
                <div className="rounded-lg border border-gray-800 bg-gray-900 h-40 flex items-center justify-center text-xs text-gray-600">No render</div>
              )}
            </div>
            {!threeQtrError && threeQtrUrl && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">3D render (3/4 view)</p>
                <img
                  src={threeQtrUrl}
                  alt="3D Render (3/4)"
                  className="w-full rounded-lg border border-gray-800 object-contain bg-gray-900 cursor-zoom-in"
                  style={{ maxHeight: 160 }}
                  onClick={() => setZoomUrl(threeQtrUrl)}
                  onError={() => setThreeQtrError(true)}
                />
              </div>
            )}
          </div>
        </div>

        {/* Score summary */}
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: "Combined Score", value: score,       cls: "text-white" },
            { label: "SSIM",           value: ssimDisplay, cls: isRenderMode ? "text-white" : "text-gray-600" },
            { label: "PBR Confidence", value: pbrConf != null ? `${Math.round(pbrConf * 100)}%` : "—", cls: "text-white" },
            { label: "AI Verdict",     value: aiVerdict ?? "—", cls: verdictColor },
          ].map(({ label, value, cls }) => (
            <div key={label} className={`rounded-lg border border-gray-800 bg-gray-900/60 p-3 space-y-1 ${!isRenderMode && label === "SSIM" ? "opacity-40" : ""}`}>
              <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
              <p className={`text-xl font-semibold tabular-nums ${cls}`}>{value}</p>
            </div>
          ))}
        </div>

        {/* Score breakdown mini-bars */}
        <div className="rounded-lg border border-gray-800 bg-gray-900/40 px-4 py-3 space-y-2.5">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Score breakdown</p>
          {scoreBarItems.map(({ label, weight, value }) => (
            <div key={label} className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-400">{label} <span className="text-gray-600">({weight})</span></span>
                <span className="text-gray-300 tabular-nums">{value != null ? `${Math.round(value * 100)}%` : "—"}</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-gray-800">
                <div
                  className="h-full rounded-full bg-orange-500/70 transition-all"
                  style={{ width: value != null ? `${Math.round(value * 100)}%` : "0%" }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* AI Notes */}
        {product.qa_ai_notes && (
          <div className="rounded-lg border border-gray-800 bg-gray-900/40 px-4 py-3">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">AI Notes</p>
            <p className="text-sm text-gray-300">{product.qa_ai_notes}</p>
          </div>
        )}

        {/* Flags — humanized */}
        {product.qa_flags && product.qa_flags.length > 0 && (
          <div className="rounded-lg border border-gray-800 bg-gray-900/40 px-4 py-3">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1.5">QA Flags</p>
            <div className="flex flex-wrap gap-1.5">
              {product.qa_flags.map((f) => (
                <span key={f} className="rounded border border-gray-700 bg-gray-900 px-2 py-0.5 text-xs text-gray-400">{humanizeFlag(f)}</span>
              ))}
            </div>
          </div>
        )}

        {/* Dimensions strip */}
        <div className="rounded-lg border border-gray-800 bg-gray-900/40 px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Dimensions</p>
          </div>
          {dims ? (
            <div className="flex gap-6 text-xs">
              <span><span className="text-gray-500">W </span><span className="text-gray-200">{fmtM(dims.width_m)}</span></span>
              <span><span className="text-gray-500">H </span><span className="text-gray-200">{fmtM(dims.height_m)}</span></span>
              <span><span className="text-gray-500">D </span><span className="text-gray-200">{fmtM(dims.depth_m)}</span></span>
            </div>
          ) : (
            <p className="text-xs text-gray-600">No dimension data</p>
          )}
          {product.consumer_specs_json === null && (
            <p className="mt-2 text-xs text-amber-500/80">Consumer specs not extracted — upload a spec sheet to improve QA accuracy.</p>
          )}
        </div>

        {err && <p className="text-sm text-red-400">{err}</p>}

        {/* Actions */}
        <div className="flex gap-3 pt-1">
          <button
            onClick={handleApprove}
            disabled={busy}
            className="flex-1 rounded-lg bg-green-700 py-2.5 text-sm font-semibold text-white hover:bg-green-600 disabled:opacity-40 transition"
          >
            {busy ? "…" : "✓ Approve"}
          </button>
          <button
            onClick={handleReject}
            disabled={busy}
            className="flex-1 rounded-lg border border-red-700 bg-red-950/40 py-2.5 text-sm font-semibold text-red-400 hover:bg-red-900/40 disabled:opacity-40 transition"
          >
            {busy ? "…" : "✗ Reject & Re-run QA"}
          </button>
          <button
            onClick={onClose}
            disabled={busy}
            className="rounded-lg border border-gray-700 px-4 py-2.5 text-sm text-gray-400 hover:bg-gray-800 disabled:opacity-40 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main Page                                                            */
/* ------------------------------------------------------------------ */

export default function AdminPipeline() {
  const { session } = useAuth();
  const jwt = session?.access_token;

  const [products, setProducts] = useState<PipelineProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [triggering, setTriggering] = useState<Set<string>>(new Set());
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null);
  const [batchSummary, setBatchSummary] = useState<{ triggered: number; running: number; complete: number } | null>(null);
  const [qaReviewProduct, setQaReviewProduct] = useState<PipelineProduct | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!jwt) return;
    if (!silent) setLoading(true);
    if (!silent) setError(null);

    try {
      const res = await fetch(
        `${SB_URL}/rest/v1/products` +
          `?select=id,name,asset_status,pbr_status,geometry_status,glb_assembly_status,` +
          `qa_status,qa_score,qa_ssim_score,qa_ai_verdict,qa_ai_notes,qa_render_url,qa_flags,` +
          `source_photos,pbr_confidence,pipeline_cost_cents,` +
          `cad_dimensions_json,consumer_specs_json,` +
          `geometry_source,material_source,` +
          `created_at,manufacturers(name)` +
          `&order=created_at.desc&limit=200`,
        { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body?.message ?? `Error ${res.status}`);
      } else {
        const data = await res.json();
        setProducts(
          data.map((row: any) => ({
            ...row,
            manufacturer_name: row.manufacturers?.name ?? "—",
          }))
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load pipeline");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [jwt]);

  const retrigger = useCallback(async (p: PipelineProduct) => {
    if (!jwt || triggering.has(p.id)) return;
    setTriggering((prev) => new Set(prev).add(p.id));

    try {
      await fetch(`${SB_URL}/functions/v1/orchestrate-pipeline`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: ANON_KEY,
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ product_id: p.id }),
      });

      // Optimistic update: mark null/failed stages as pending so the UI reflects
      // the trigger. Only stages that apply — showing "pending" on a pergola's
      // PBR would promise a job that is never going to run.
      setProducts((prev) =>
        prev.map((row) => {
          if (row.id !== p.id) return row;
          const optimistic = (stage: "pbr" | "geometry" | "glb_assembly", status: PipelineStatus) =>
            isStageApplicable(row, stage) && (status === null || status === "failed") ? "pending" : status;
          return {
            ...row,
            pbr_status:          optimistic("pbr", row.pbr_status),
            geometry_status:     optimistic("geometry", row.geometry_status),
            glb_assembly_status: optimistic("glb_assembly", row.glb_assembly_status),
          };
        })
      );
    } catch (err) {
      console.error("[retrigger] orchestrate-pipeline failed:", err);
    } finally {
      setTriggering((prev) => { const next = new Set(prev); next.delete(p.id); return next; });
    }
  }, [jwt, triggering]);

  // Optimistic update after QA approve/reject from the modal
  const handleQaAction = useCallback((id: string, action: "approved" | "rejected") => {
    setProducts((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        return {
          ...row,
          qa_status:    action === "approved" ? "approved" : "pending",
          asset_status: action === "approved" ? "ready"    : row.asset_status,
        };
      })
    );
  }, []);

  const runAllPending = useCallback(async () => {
    if (!jwt || batchRunning) return;

    const candidates = products.filter((p) => needsRetrigger(p) && p.asset_status !== "ready");
    if (candidates.length === 0) return;

    const confirmed = window.confirm(
      `Run pipeline for ${candidates.length} product${candidates.length !== 1 ? "s" : ""}?\n\nThis will trigger PBR, geometry, and assembly for all pending products.`,
    );
    if (!confirmed) return;

    setBatchRunning(true);
    setBatchProgress({ current: 0, total: candidates.length });
    setBatchSummary(null);

    const summary = { triggered: 0, running: 0, complete: 0 };

    for (let i = 0; i < candidates.length; i++) {
      const p = candidates[i];
      setBatchProgress({ current: i + 1, total: candidates.length });

      try {
        const res = await fetch(`${SB_URL}/functions/v1/orchestrate-pipeline`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: ANON_KEY,
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({ product_id: p.id }),
        });
        if (res.ok) {
          const data = await res.json() as { status: string };
          if (data.status === "triggered")       summary.triggered++;
          else if (data.status === "already_running")  summary.running++;
          else if (data.status === "already_complete") summary.complete++;
        }
      } catch (err) {
        console.error(`[batch] product ${p.id} failed:`, err);
      }

      if (i < candidates.length - 1) {
        await new Promise((r) => setTimeout(r, 500));
      }
    }

    setBatchSummary(summary);
    setBatchRunning(false);
    setBatchProgress(null);
    await load(true);
  }, [jwt, batchRunning, products, load]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => load(true), 15_000);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  const filtered = products.filter((p) => {
    const matchesStatus =
      filterStatus === "all" || (p.asset_status ?? "unknown") === filterStatus;
    const q = search.trim().toLowerCase();
    const matchesSearch =
      !q ||
      p.name.toLowerCase().includes(q) ||
      (p.manufacturer_name ?? "").toLowerCase().includes(q);
    return matchesStatus && matchesSearch;
  });

  const counts = products.reduce<Record<string, number>>(
    (acc, p) => {
      const s = p.asset_status ?? "unknown";
      acc[s] = (acc[s] ?? 0) + 1;
      return acc;
    },
    {}
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Pipeline Status</h1>
          <p className="mt-0.5 text-sm text-gray-400">
            Real-time status of each product's pipeline stages. Parametric
            products skip stages that do not apply to them — those read
            &ldquo;n/a&rdquo;, not pending.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-700"
            />
            Auto-refresh (15s)
          </label>
          <button
            onClick={() => load()}
            disabled={loading}
            className="rounded-lg border border-gray-700 bg-gray-900 px-3 py-1.5 text-sm hover:bg-gray-800 disabled:opacity-50 transition"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
          <button
            onClick={runAllPending}
            disabled={batchRunning || products.filter((p) => needsRetrigger(p) && p.asset_status !== "ready").length === 0}
            className="rounded-lg border border-orange-700 bg-orange-950/40 px-3 py-1.5 text-sm text-orange-400 hover:bg-orange-900/40 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {batchRunning ? "Running…" : "Run All Pending"}
          </button>
        </div>
      </div>

      {/* Batch progress */}
      {batchProgress && (
        <div className="rounded-lg border border-blue-800 bg-blue-950/30 px-4 py-3">
          <p className="mb-2 text-sm font-medium text-blue-300">
            Triggering {batchProgress.current} / {batchProgress.total} products…
          </p>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-800">
            <div
              className="h-full rounded-full bg-blue-500 transition-all"
              style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Batch summary */}
      {batchSummary && !batchProgress && (
        <div className="rounded-lg border border-green-800 bg-green-950/30 px-4 py-3 text-sm">
          <span className="font-medium text-green-300">Batch complete — </span>
          <span className="text-gray-300">
            Triggered: <strong className="text-green-400">{batchSummary.triggered}</strong>
            {" · "}Already running: <strong className="text-yellow-400">{batchSummary.running}</strong>
            {" · "}Already complete: <strong className="text-gray-500">{batchSummary.complete}</strong>
          </span>
          <button
            onClick={() => setBatchSummary(null)}
            className="ml-4 text-xs text-gray-600 hover:text-gray-400"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Stage legend */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
        <span><span className="font-medium text-gray-400">Asset</span> — overall readiness</span>
        <span><span className="font-medium text-gray-400">PBR</span> — material map extraction (n/a for swatch-library finishes)</span>
        <span><span className="font-medium text-gray-400">Geometry</span> — mesh reconstruction, or parametric generation</span>
        <span><span className="font-medium text-gray-400">Assembly</span> — GLB/USDZ packaging</span>
        <span><span className="font-medium text-gray-400">QA</span> — SSIM + Claude Vision audit</span>
      </div>

      {/* Status summary chips (filter by asset_status) */}
      <div className="flex flex-wrap gap-2">
        {(["all", "pending", "processing", "ready", "complete", "failed"] as const).map((s) => {
          const isAll = s === "all";
          const count = isAll ? products.length : (counts[s] ?? 0);
          const meta = isAll
            ? { label: "All", classes: "border-gray-700 text-gray-300" }
            : STATUS_META[s] ?? { label: s, classes: "border-gray-700 text-gray-400" };
          return (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={[
                "rounded-full border px-3 py-1 text-xs font-medium transition",
                filterStatus === s
                  ? `${meta.classes} ring-1 ring-offset-1 ring-offset-gray-950`
                  : "border-gray-800 text-gray-500 hover:border-gray-700",
              ].join(" ")}
            >
              {meta.label} ({count})
            </button>
          );
        })}
      </div>

      {/* QA review modal */}
      {qaReviewProduct && jwt && (
        <QaReviewModal
          product={qaReviewProduct}
          jwt={jwt}
          onClose={() => setQaReviewProduct(null)}
          onAction={handleQaAction}
        />
      )}

      {error && (
        <div className="rounded-lg border border-red-800 bg-red-950/40 px-4 py-3">
          <p className="text-sm font-medium text-red-400">Failed to load pipeline</p>
          <p className="mt-0.5 text-xs text-red-500">{error}</p>
        </div>
      )}

      <input
        type="text"
        placeholder="Search by name or manufacturer…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ background: "#111827", color: "#f9fafb" }}
        className="w-full sm:w-72 rounded-lg border border-gray-700 px-3 py-2 text-sm placeholder-gray-600 focus:border-orange-500 focus:outline-none"
      />

      {/* Table */}
      {loading ? (
        <div className="text-sm text-gray-400">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-gray-800 py-12 text-center text-sm text-gray-500">
          No products match your filter.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-800">
          <table className="w-full text-sm">
            <thead className="bg-gray-900/60">
              <tr className="border-b border-gray-800">
                <th className="px-4 py-3 text-left font-medium text-gray-400">Product</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400">Manufacturer</th>
                <th className="px-3 py-3 text-left font-medium text-gray-400">Stages</th>
                <th className="px-3 py-3 text-left font-medium text-gray-400">Asset</th>
                <th className="px-3 py-3 text-left font-medium text-gray-400">PBR</th>
                <th className="px-3 py-3 text-left font-medium text-gray-400">Geometry</th>
                <th className="px-3 py-3 text-left font-medium text-gray-400">Assembly</th>
                <th className="px-3 py-3 text-left font-medium text-gray-400">QA</th>
                <th className="px-3 py-3 text-left font-medium text-gray-400">Confidence</th>
                <th className="px-3 py-3 text-left font-medium text-gray-400">Cost</th>
                <th className="px-4 py-3 text-left font-medium text-gray-400">Created</th>
                <th className="px-3 py-3 text-left font-medium text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  className="border-t border-gray-800 hover:bg-gray-900/40 transition"
                >
                  <td className="px-4 py-3 font-medium max-w-[200px] truncate" title={p.name}>
                    {p.name}
                  </td>
                  <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{p.manufacturer_name}</td>
                  <td className="px-3 py-3"><StageProgress product={p} /></td>
                  <td className="px-3 py-3"><StatusBadge status={p.asset_status} /></td>
                  <td className="px-3 py-3">
                    {isStageApplicable(p, "pbr")
                      ? <StatusBadge status={p.pbr_status} />
                      : <NotApplicable reason="Finish comes from the swatch library — no Stability texture generation runs for this product." />}
                  </td>
                  <td className="px-3 py-3">
                    <StatusBadge
                      status={p.geometry_status}
                      title={
                        p.geometry_status === "insufficient_photos"
                          ? `Only ${p.source_photos?.length ?? 0} source photo${(p.source_photos?.length ?? 0) === 1 ? "" : "s"} — at least 3 are required for reliable 3D reconstruction. Upload more angles and retry.`
                          : undefined
                      }
                    />
                  </td>
                  <td className="px-3 py-3"><StatusBadge status={p.glb_assembly_status} /></td>
                  <td className="px-3 py-3">
                    <QaBadge status={p.qa_status} score={p.qa_score} />
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-400">
                    {p.pbr_confidence != null
                      ? `${(p.pbr_confidence * 100).toFixed(0)}%`
                      : <span className="text-gray-700">—</span>}
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-400 whitespace-nowrap">
                    {p.pipeline_cost_cents?.total
                      ? formatCost(p.pipeline_cost_cents.total)
                      : <span className="text-gray-700">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                    {formatDate(p.created_at)}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {needsRetrigger(p) && (
                        <button
                          onClick={() => retrigger(p)}
                          disabled={triggering.has(p.id)}
                          className="rounded border border-orange-700 bg-orange-950/40 px-2 py-0.5 text-xs text-orange-400 hover:bg-orange-900/40 disabled:opacity-50 transition whitespace-nowrap"
                        >
                          {triggering.has(p.id) ? "Triggering…" : "Retrigger"}
                        </button>
                      )}
                      {(p.qa_status === "review" || p.qa_status === "failed") && jwt && (
                        <button
                          onClick={() => setQaReviewProduct(p)}
                          className="rounded border border-amber-700 bg-amber-950/40 px-2 py-0.5 text-xs text-amber-400 hover:bg-amber-900/40 transition whitespace-nowrap"
                        >
                          Review QA
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between border-t border-gray-800 px-4 py-2 text-xs text-gray-600">
            <span>Showing {filtered.length} of {products.length} products</span>
            <span>
              Est. total spend:{" "}
              <span className="font-medium text-gray-400">
                {formatCost(
                  filtered.reduce((sum, p) => sum + (p.pipeline_cost_cents?.total ?? 0), 0)
                )}
              </span>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
